/**
 * SafetyScheduleReplyCapture · iter303ak-fix28 — Work Booked · T40 · Action 20
 * ──────────────────────────────────────────────────────────────────────────
 * Log the safety supplier's reply: Confirmed / Rescheduled / Pending + note.
 * Persists to parent appraisal as `safety_schedule_reply`.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle2, MessageCircle, AlertTriangle } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const STATUSES = [
  { key: 'confirmed',   label: 'Confirmed',
    active: 'bg-emerald-600 text-white border-emerald-700',
    inactive: 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50' },
  { key: 'rescheduled', label: 'Rescheduled',
    active: 'bg-amber-600 text-white border-amber-700',
    inactive: 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50' },
  { key: 'pending',     label: 'Pending',
    active: 'bg-slate-600 text-white border-slate-700',
    inactive: 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50' },
];

export default function SafetyScheduleReplyCapture({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentAppraisalId = capturedData?.appraisal_id;
  const persistField = params.persist_field || 'safety_schedule_reply';

  const [status, setStatus] = useState(value?.status || '');
  const [note, setNote] = useState(value?.note || '');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  useEffect(() => {
    if (onChange) onChange({ status, note, at: status ? new Date().toISOString() : null });
  }, [status, note]);   

  const persistAndContinue = async () => {
    setSaving(true);
    setSaveErr('');
    try {
      const payload = { status, note, at: new Date().toISOString() };
      if (parentAppraisalId) {
        await api.patch(`/appraisals/${parentAppraisalId}`, {
          [persistField]: payload,
        });
      }
      onNext();
    } catch (e) {
      setSaveErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const canContinue = status && status !== 'pending';

  return (
    <div className="space-y-4" data-testid="wf-safety-schedule-reply">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
        <div className="flex items-start gap-3">
          <MessageCircle className="w-5 h-5 mt-0.5 shrink-0 text-indigo-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-indigo-900">Log Safety Supplier Reply</div>
            <div className="text-sm mt-1 text-indigo-800">
              {params.prompt || 'Log the safety supplier\'s response. Continue enabled once marked Confirmed or Rescheduled.'}
            </div>
          </div>
        </div>
      </div>

      <div className={`p-4 border rounded-lg ${canContinue ? 'bg-emerald-50/40 border-emerald-200' : 'bg-white border-slate-200'}`}>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {STATUSES.map(s => {
            const active = status === s.key;
            return (
              <button key={s.key}
                type="button"
                onClick={() => setStatus(s.key)}
                data-testid={`wf-sr-status-${s.key}`}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${active ? s.active : s.inactive}`}>
                {s.label}
              </button>
            );
          })}
        </div>
        <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Note (optional)</label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. crew booked, rescheduled to alt date, awaiting supervisor sign-off …"
          data-testid="wf-sr-note"
          className="w-full px-2 py-1 text-xs border border-slate-300 rounded" rows={3} />
      </div>

      {saveErr && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> <span>{saveErr}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={persistAndContinue} disabled={!canContinue || saving}
            data-testid="wf-sr-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> {saving ? 'Saving…' : 'Save & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
